import { useMutation, useQuery } from "@tanstack/react-query";
import {
  completeQuestionBankRewardedAd,
  fetchQuestionBankInstructions,
  fetchQuestionBankQuestionNumberList,
  fetchQuestionBankQuestionReference,
  fetchQuestionBankQuestions,
  saveQuestionBankGuessAnswer,
  saveQuestionBankQuestionAnswer,
  storeQuestionBankQuestionTime,
} from "../services/questionBankQuestionService";

function extractPagination(payload = {}) {
  const pagination = payload?.pagination || {};

  return {
    currentPage: Number(pagination?.currentPage || pagination?.current_page || payload?.current_page || 1),
    perPage: Number(pagination?.perPage || pagination?.per_page || payload?.per_page || 10),
    totalRecords: Number(pagination?.totalRecords || pagination?.total_records || payload?.total_records || 0),
    totalPages: Number(pagination?.totalPages || pagination?.total_pages || payload?.total_pages || 1),
    nextPage: Number(pagination?.nextPage || pagination?.next_page || payload?.next_page || 0),
    previousPage: Number(
      pagination?.previousPage ||
        pagination?.previous_page ||
        pagination?.prevPage ||
        pagination?.prev_page ||
        payload?.previous_page ||
        payload?.prev_page ||
        0
    ),
  };
}

function getLocalizedText(item = {}, baseField) {
  return (
    item?.[`${baseField}_en`] ||
    item?.[baseField] ||
    item?.[`${baseField}_hi`] ||
    ""
  );
}

function normalizeOption(option = {}, index = 0) {
  return {
    id: Number(option?.option_id || option?.id || index + 1),
    label: getLocalizedText(option, "option_name") || option?.title || `Option ${index + 1}`,
    isCorrect: String(option?.is_option_correct || "").toUpperCase() === "Y",
    isSelected: Number(option?.user_selected_option || 0) === 1,
  };
}

function normalizeQuestion(item = {}, index = 0, page = 1, perPage = 0) {
  const resolvedIndex = Number(item?.question_index || item?.question_number || item?.count || 0);
  const seq = resolvedIndex || ((Math.max(page, 1) - 1) * Math.max(perPage, 0)) + index + 1;
  const questionId = Number(item?.question_id || item?.id || seq);

  return {
    id: questionId,
    question_id: questionId,
    serialNo: seq,
    question_index: seq,
    question_number: seq,
    question: getLocalizedText(item, "question_name") || item?.question || "Question will be available soon.",
    questionHtml: getLocalizedText(item, "question_name") || item?.question || "Question will be available soon.",
    questionType: String(item?.question_type || ""),
    questionImage: item?.question_image || "",
    questionStatementOne: getLocalizedText(item, "question_statement_one"),
    questionStatementTwo: getLocalizedText(item, "question_statement_two"),
    options: Array.isArray(item?.question_option) ? item.question_option.map(normalizeOption) : [],
    rawOptions: Array.isArray(item?.question_option) ? item.question_option : [],
    paymentStatus: Number(item?.payment_status ?? 0),
    isLocked:
      (String(item?.free_paid || "").toUpperCase() !== "F" && Number(item?.payment_status) !== 1 && Number(item?.is_locked ?? 0) === 1) ||
      (String(item?.free_paid || "").toUpperCase() === "P" && Number(item?.payment_status ?? 0) !== 1),
    isBookmarked: Number(item?.bookmark_status ?? item?.is_bookmarked ?? 0) === 1,
    isLiked: Number(item?.like_status ?? item?.is_liked ?? 0) === 1,
    isAnswered: Number(item?.is_question_answered ?? item?.question_answered ?? 0) === 1,
    isMarkedForReview: Number(item?.is_marked_for_review ?? item?.review_status ?? 0) === 1,
    isGuess: Number(item?.is_guess_answer ?? item?.guess_status ?? 0) === 1,
    selectedOptionId:
      Number(
        item?.selected_option_id ||
          item?.user_selected_option_id ||
          item?.question_option?.find((option) => Number(option?.user_selected_option || 0) === 1)?.option_id ||
          0
      ) || null,
    likeCount: Number(item?.total_likes ?? item?.like_count ?? item?.likes ?? 0),
    viewCount: Number(item?.total_views ?? item?.view_count ?? item?.views ?? 0),
    reportCount: Number(item?.total_reports ?? item?.report_count ?? 0),
    notesCount: Number(item?.total_notes ?? item?.notes_count ?? 0),
    questionTime: Number(item?.question_time || 0),
    free_paid: item?.free_paid || "",
    isPaidBlocked:
      String(item?.free_paid || "").toUpperCase() === "P" && Number(item?.payment_status ?? 0) !== 1,
    isPyq: Number(item?.is_pyq || 0) === 1,
    pyqMappings: Array.isArray(item?.pyq_mappings) ? item.pyq_mappings : [],
    raw: item,
  };
}

export function useQuestionBankPracticeQuestions(params = {}, options = {}) {
  return useQuery({
    queryKey: ["questionBankPracticeQuestions", params],
    queryFn: async () => {
      const { payload } = await fetchQuestionBankQuestions(params);
      const pagination = extractPagination(payload);
      const rawQuestions =
        payload?.questions ||
        payload?.question_list ||
        payload?.questions_list ||
        payload?.data ||
        [];

      return {
        stateName: payload?.state_name || "",
        raw: payload,
        subjectName: payload?.subject_name || "",
        totalQuestions: Number(payload?.total_questions || payload?.question_count || pagination.totalRecords || 0),
        guessScore: payload?.guess_score || "0%",
        pagination,
        questions: (Array.isArray(rawQuestions) ? rawQuestions : []).map((question, index) =>
          normalizeQuestion(question, index, pagination.currentPage, pagination.perPage)
        ),
      };
    },
    enabled: options.enabled ?? true,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useQuestionBankPracticePalette(params = {}, options = {}) {
  return useQuery({
    queryKey: ["questionBankPracticePalette", params],
    queryFn: async () => {
      const { payload } = await fetchQuestionBankQuestionNumberList(params);
      return payload || {};
    },
    enabled: options.enabled ?? true,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useQuestionBankInstructions(params = {}, options = {}) {
  return useQuery({
    queryKey: ["questionBankInstructions", params],
    queryFn: async () => {
      const { payload } = await fetchQuestionBankInstructions(params);
      return payload;
    },
    enabled: options.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useQuestionBankQuestionReference(params = {}, options = {}) {
  return useQuery({
    queryKey: ["questionBankQuestionReference", params, options.fontSize],
    queryFn: async () => {
      const { payload } = await fetchQuestionBankQuestionReference(params);
      return payload;
    },
    enabled: options.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useQuestionBankPracticeMutations() {
  return {
    saveAnswer: useMutation({
      mutationFn: saveQuestionBankQuestionAnswer,
    }),
    saveTime: useMutation({
      mutationFn: storeQuestionBankQuestionTime,
    }),
    saveGuess: useMutation({
      mutationFn: saveQuestionBankGuessAnswer,
    }),
    completeRewardedAd: useMutation({
      mutationFn: completeQuestionBankRewardedAd,
    }),
  };
}
